<template>
  <div class="integration-section">
    <div class="grid-column ai-c jc-s sac__font-20 semi-bold">
      Create Custom Field for Comfort Credits
      <SACTooltipItem class="margin-top-4" color="#727A8F" :icon-size="20">
        <template #tooltip>
          <div class="sac__font-12 med">
            Recommended Custom Field Name:
            <div class="sac__font-12 f-primary semi-bold">SmartAC.com Comfort Credit</div>
          </div>
        </template>
      </SACTooltipItem>
    </div>
    <div class="sac__font-14 margin-top-16">
      Please create a Custom Field in
      <a
        :href="customFieldHref"
        class="sac__font-16 f-primary med underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        ServiceTitan
      </a>
      to store the balance of Comfort Credits. Please ensure that both the Customer Record and Job
      Record are selected when creating a custom field. Once created, enter the exact name of the
      field below. This ensures Comfort Credits are properly mapped and synchronized between the two
      systems.
    </div>
    <div class="grid-column even gap-16 margin-top-16">
      <SACInput
        placeholder="ServiceTitan Custom Field Name"
        field="customFieldName"
        :value="customFieldName"
        :change="changeField"
        :error="customFieldError"
      />
      <div v-if="saving">
        <img src="@shared/assets/img/general/loading.svg" alt="loading" />
      </div>
      <div v-else-if="customFieldStId">
        <img src="@shared/assets/img/general/check-green.svg" alt="check" class="img-size-24" />
      </div>
      <div v-else>
        <button
          type="button"
          :disabled="isDisabled"
          :class="['sac__button-med bg-default', isDisabled ? 'disabled' : '']"
          @click="saveCustomField"
        >
          Add Custom Field
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import SACTooltipItem from '@shared/components/sac/SACTooltip.vue';
import SACInput from '@shared/components/sac/SACInput.vue';
import { mapState } from 'vuex';
import MapType from '@shared/lib/constant/MapType';

export default {
  name: 'AddCustomField',
  components: {
    SACInput,
    SACTooltipItem,
  },
  watch: {
    customFieldName(oldVal, newVal) {
      if (newVal !== oldVal && this.customFieldName !== this.customFieldMapping.internalId) {
        this.customFieldStId = null;
      } else {
        this.customFieldStId = this.customFieldMapping.externalId;
        this.customFieldError = null;
      }
    },
    customFieldMapping: {
      immediate: true,
      handler(newVal) {
        if (newVal?.internalId) {
          this.customFieldName = newVal.internalId;
          this.customFieldStId = newVal.externalId;
        }
      },
    },
  },
  data() {
    return {
      customFieldName: null,
      customFieldStId: null,
      customFieldError: null,
      saving: false,
    };
  },
  computed: {
    ...mapState({
      customFieldMapping: (state) => state.serviceTitan.customFieldMapping,
    }),
    customFieldHref() {
      return `${process.env.ST_URL}/#/Settings/CustomFieldTypes/Add`;
    },
    isDisabled() {
      return !this.customFieldName || this.saving;
    },
  },
  methods: {
    async verifyCustomField() {
      return await this.$store.dispatch('getSTCustomFieldId', this.customFieldName);
    },
    async saveCustomField() {
      this.saving = true;
      this.customFieldError = null;
      try {
        const stCustomFieldId = await this.verifyCustomField();
        if (!stCustomFieldId) {
          this.customFieldError = 'Ensure that a custom field already exists in ServiceTitan';
          return;
        }
        this.customFieldStId = stCustomFieldId;
        const payload = [
          {
            ...this.customFieldMapping,
            internalId: this.customFieldName,
            externalId: stCustomFieldId,
            externalSource: 'Service Titan',
            mapType: MapType.COMFORT_CREDIT_BALANCE,
          },
        ];
        await this.$store.dispatch('saveMapping', payload);
      } finally {
        this.saving = false;
      }
    },
    changeField(f, v) {
      this[f] = typeof v === 'string' ? v.trim() : v;
    },
  },
};
</script>
